<template>
<m-pageframe
    :title="title"
>
<v-container
    fluid
    style="padding-left: 15px; padding-right: 15px;"
>

    <v-text-field
        autocomplete="off"
        label="Search clubs by name, description, or ID"
        prepend-icon="mdi-magnify"
        v-model="full_text"
        v-on:input="update"
        clearable
        style="margin-bottom: 20px;"
    >
        <v-menu
            bottom
            offset-y
            slot="append-outer"
        >
            <template v-slot:activator="{ on }">
                <v-icon
                    v-on="on"
                >
                    mdi-dots-vertical
                </v-icon>
            </template>

            <v-list>
                <v-list-item
                    v-on:click="onClickRefreshClubs()"
                >
                    <v-list-item-title>Refresh clubs from {{crm_name}}...</v-list-item-title>
                </v-list-item>
            </v-list>
        </v-menu>
    </v-text-field>

    <!-- Selected Clubs Summary - Always visible when in bulk message mode -->
    <v-card
        v-if="add_to_bulk_message != null"
        class="mb-6"
        outlined
    >
        <v-card-title class="pb-2">
            <v-icon class="mr-2" color="primary">mdi-account-group</v-icon>
            Selected Clubs
            <v-spacer/>
            <v-chip
                :color="selected_clubs.length > 0 ? 'primary' : 'grey lighten-2'"
                :text-color="selected_clubs.length > 0 ? 'white' : 'grey darken-1'"
                small
            >
                {{selected_clubs.length}} selected
            </v-chip>
        </v-card-title>

        <v-card-text class="pt-0">
            <div v-if="selected_clubs.length === 0" class="text--secondary">
                Select clubs below to add their customers to your bulk message
            </div>
            <div v-else>
                <v-chip
                    v-for="(item, index) in selected_clubs"
                    :key="'sc_'+index"
                    class="mr-2 mb-2"
                    close
                    color="primary"
                    outlined
                    v-on:click:close="onClickClose(item)"
                >
                    {{item.text}}
                </v-chip>
            </div>
        </v-card-text>

        <v-card-actions v-if="selected_clubs.length > 0">
            <v-spacer/>
            <v-btn
                :color="is_remove_mode ? 'warning' : 'primary'"
                :disabled="selected_clubs.length == 0"
                v-on:click="onClickAddCustomersToBulkMessage"
            >
                {{ is_remove_mode ? 'Remove Customers from Bulk Message' : 'Add Customers to Bulk Message' }}
            </v-btn>
        </v-card-actions>
    </v-card>


    <!-- Enhanced Clubs Table -->
    <v-data-table
        v-if="results != null"
        :headers="computedHeaders"
        :items="results"
        :items-per-page="50"
        :no-data-text="computedNoDataText"
        :footer-props="footerProps"
        class="enhanced-data-table enhanced-data-table--clubs elevation-1"
    >


        <!-- Club Info Column -->
        <template v-slot:item.club_info="{ item }">
            <div
                class="club-row-content"
                :class="{ 'club-row-content--selected': isSelected(item) }"
                v-on:click="add_to_bulk_message != null ? toggleSelection(item) : null"
            >
                <div class="d-flex align-center">
                    <v-icon
                        class="mr-3"
                        :color="isSelected(item) ? 'primary' : 'grey darken-1'"
                        size="20"
                    >
                        mdi-account-group
                    </v-icon>
                    <div class="flex-grow-1">
                        <div class="club-name">{{item.label}}</div>
                        <div v-if="item.club_id" class="club-id">ID: {{item.club_id}}</div>
                        <div v-else class="club-id text--disabled">No ID available</div>
                    </div>
                </div>
            </div>
        </template>

        <!-- Actions Column -->
        <template v-slot:item.actions="{ item }">
            <v-menu
                bottom
                offset-y
            >

            <template v-slot:activator="{ on }">

                <v-icon
                    v-on="on"
                >
                    mdi-dots-vertical
                </v-icon>

            </template>

            <v-list>
                <v-list-item
                    v-on:click="onClickViewInCrm(item)"
                    v-if="item.url_view_in_crm != null"
                >
                    <v-list-item-title>View in {{crm_name}}...</v-list-item-title>
                </v-list-item>

                <v-list-item
                    v-on:click="onClickTechnicalData(item)"
                >
                    <v-list-item-title>Technical Details...</v-list-item-title>
                </v-list-item>

                <v-list-item
                    v-on:click="onClickTechnicalDataRaw(item)"
                >
                    <v-list-item-title>Technical Details (raw data sent by {{crm_name}})...</v-list-item-title>
                </v-list-item>

            </v-list>

            </v-menu>
        </template>

        <!-- Selection Column Template -->
        <template v-if="add_to_bulk_message != null" v-slot:item.selected="{ item }">
            <v-simple-checkbox
                :value="isSelected(item)"
                @input="onSelectionChange({ selected: $event, item })"
                color="primary"
                :ripple="false"
            />
        </template>

    </v-data-table>

    

</v-container>
</m-pageframe>
</template>

<script>
const _ = require('lodash');

export default {

    data() {
        return {
            full_text: null,
            results: [],

            add_to_bulk_message: null,
            selected_clubs: [],
        }
    },


    mounted () {
        this.updateAddToBulkMessage();
        this.doUpdate();
    },

    methods: {
        update: _.debounce(function(){
            this.doUpdate();
        }, 751),
        
        doUpdate()
        {
            // Determine if we should use live data (for Offset when adding to bulk messages)
            const use_live_data = this.shouldUseLiveData();

            this.$rc.rest(
                'LIST: /api/v1/integrations/crm/club-meta-data',

                {
                    'full_text': this.full_text,
                    'organization_id' : this.$route.params.organization_id,
                    'content_type' : this.$route.params.content_type,
                    'use_live_data': use_live_data,
                },

                (data) => {
                    this.results = data.results;
                },

                () => {
                    this.$rc.alert('Error','Unable to search clubs');
                },
            );
        },

        shouldUseLiveData()
        {
            // Use live data only for Offset integration when adding to bulk messages
            // Note: content_type in URL is lowercase, but we need to check for both cases
            const content_type = this.$route.params.content_type;
            const is_offset = content_type === 'OFFSET' || content_type === 'offset';
            const has_bulk_message = this.$route.query.add_to_bulk_message_id != null;
            return is_offset && has_bulk_message;
        },

        updateAddToBulkMessage()
        {
            if ( this.$route.query.add_to_bulk_message_id == null )
            {
                this.add_to_bulk_message = null;
                return;
            }

            this.$rc.rest({
                'spinner': false,
                'endpoint': 'GET: /api/v1/bulk/messages/get-profile/:bulk_message_id',
            
                'params' : {
                        'bulk_message_id': this.$route.query.add_to_bulk_message_id,
                    },
            
                'on_success': (data) => {
                        this.add_to_bulk_message = data;
                    },
            
                'on_error': 'Unable to get bulk message meta data',
            });
        },

        onClickViewInCrm(item)
        {
            window.open(item.url_view_in_crm,"_blank");
        },

        onClickTechnicalData(item)
        {
            this.$rc.copy_code_dialog({
                    'title': 'Technical Details',
                    'object': item,
                });
        },

        onClickTechnicalDataRaw(item)
        {
            try {
                let object = JSON.parse(item.raw_content);

                this.$rc.copy_code_dialog({
                    'title': 'Technical Details',
                    'object': object,
                });
            } 
            catch (e) 
            {
                this.$rc.copy_code_dialog({
                    'title': 'Technical Details',
                    'code': item.raw_content,
                });
            }
        },

        onClickRefreshClubs()
        {
            this.$rc.startProcess({
                'type': 'crm-sync-club-meta-data',

                'params': {
                    'organization_id' : this.$route.params.organization_id,
                    'content_type' : this.$route.params.content_type,
                },
            
                'title': 'Refreshing clubs from '+this.crm_name,
                'finished_button_text': 'Ok',
                'failed_button_text': 'Ok',
            
                status: (pte) => {
                    if ( pte.state == 'failed' )
                        return "Club regresh failed";

                    return 'Club meta data downloaded: '+pte.progress_counter;
                },
            
                on_finished: () => {
                    this.doUpdate();
                },

                on_failed: () => {
                    this.doUpdate();
                },
            
            });
        },

        onChangeSelected(val, item)
        {
            this.selected_clubs = this.selected_clubs.filter((el) => el.value != item.club_id);

            if ( val == false )
            {    
                return;
            }

            this.selected_clubs.push({
                'text': item.label,
                'value': item.club_id,
            });

            this.$forceUpdate();
        },

        onClickClose(item)
        {
            this.selected_clubs = this.selected_clubs.filter((el) => el.value != item.value);
            this.$forceUpdate();
        },

        isSelected(item)
        {
             return this.selected_clubs.some((el) => el.value == item.club_id);
        },

        toggleSelection(item) {
            if (this.add_to_bulk_message == null) return;

            const isCurrentlySelected = this.isSelected(item);
            this.onChangeSelected(!isCurrentlySelected, item);
        },

        onSelectionChange({ selected, item }) {
            this.onChangeSelected(selected, item);
        },

        onClickAddCustomersToBulkMessage()
        {
            this.$rc.confirm({
                'title': 'Warning: Check your recipients carefully',
                'body': '<p>RedChirp does its best to create an accurate and complete list of customers with active subscriptions to <b>any</b> of club(s) you have specified.  Despire our best efforts, however, such a list may be inaccurate or incomplete.</p><p>It is important that you check your recipients <b>very carefully</b> before sending any bulk message.</p>',
                'btn_text_ok': 'I understand',
                'btn_text_cancel': 'Cancel',
                'on_ok': () => {
                    this.doAddCustomersToBulkmessage();
                },
            });
        },

        doAddCustomersToBulkmessage()
        {
            // Determine if we should use live data (for Offset when adding to bulk messages)
            const use_live_data = this.shouldUseLiveData();

            this.$rc.startProcess({
                'type': 'crm-create-excel-preview-of-customers-with-active-club-membership-for-bulk-message',

                'params': {
                    'organization_id' : this.$route.params.organization_id,
                    'content_type' : this.$route.params.content_type,
                    'bulk_message_id': this.add_to_bulk_message.bulk_message_id,
                    'club_ids': this.selected_clubs.map(el => el.value),
                    'club_labels': this.selected_clubs.map(el => el.text),
                    'use_live_data': use_live_data,
                    'import_execution_mode': this.is_remove_mode ? 'remove' : 'add-or-update',
                },
            
                'title': this.is_remove_mode ? 'Listing club members to remove' : 'Listing customers with active club memberships',
                'finished_button_text': 'Preview Recipients',
                'failed_button_text': 'Cancel',
            
                status: (pte) => {
                    return 'Customer phone numbers: '+pte.progress_counter;
                },
            
                on_finished: (pte) => {
                    this.$router.push({
                        'name': 'bulk-message-recipients-import-upload',
                        'params' : {
                            'organization_id' : pte.output.organization_id,
                            'import_id' : pte.output.import_id
                        },
                        'query' : {
                            'bulk_message_id' : pte.output.bulk_message_id,
                            'return_to' : 'view-bulk-message',
                            'mode': this.is_remove_mode ? 'remove' : undefined,
                        }
                    });
                },
            
                on_failed: () => {
                },
            
            });
        },
    
    },

    computed: {
        is_remove_mode() {
            return this.$route.query.mode === 'remove';
        },

        title() {
            if ( this.add_to_bulk_message != null )
            {
                const action = this.is_remove_mode ? 'Remove' : 'Add';
                return action + " customers with "+this.$rc.pretty(this.$route.params.content_type, 'enum_integration_name')+" active club membership " + (this.is_remove_mode ? 'from' : 'to') + " bulk message \""+this.add_to_bulk_message.label+"\"";
            }

            return 'Search '+this.$rc.pretty(this.$route.params.content_type, 'enum_integration_name')+' Clubs';
        },

        crm_name() {
            return this.$rc.pretty(this.$route.params.content_type, 'enum_integration_name');
        },

        base_headers() {
            return [
                {
                    text: "Club Information",
                    value: "club_info",
                    sortable: false,
                    class: "club-info-column"
                },
                {
                    text: "Actions",
                    value: "actions",
                    width: "100px",
                    sortable: false,
                    class: "text-center"
                }
            ];
        },

        computedHeaders() {
            let headers = [];

            // Add selection column if in bulk message mode
            if (this.add_to_bulk_message != null) {
                headers.push({
                    text: "",
                    value: "selected",
                    width: "60px",
                    sortable: false,
                    class: "text-center"
                });
            }

            headers.push(...this.base_headers);
            return headers;
        },

        computedNoDataText() {
            if (this.full_text) {
                return `No clubs found matching "${this.full_text}"`;
            }
            return 'No clubs available';
        },

        footerProps() {
            return {
                itemsPerPageText: 'Clubs per page:',
                itemsPerPageOptions: [25, 50, 100],
                disableItemsPerPage: false,
                disablePagination: false
            };
        },

        enhanced_headers() {
            let headers = [];

            if (this.add_to_bulk_message != null) {
                headers.push({
                    text: "",
                    value: "selected",
                    width: "60px",
                    sortable: false,
                    class: "text-center"
                });
            }

            headers.push(...this.base_headers);
            return headers;
        },

        no_data_message() {
            if (this.full_text) {
                return `No clubs found matching "${this.full_text}"`;
            }
            return 'No clubs available';
        }
    },
}
</script>

<style lang="scss" scoped>
.enhanced-clubs-table {
    ::v-deep .v-data-table__wrapper {
        border-radius: 8px;
    }

    ::v-deep tbody tr {
        transition: all 0.2s ease-in-out;

        &:hover {
            background-color: rgba(var(--v-primary-base), 0.04) !important;
        }

        &.table-row--selected {
            background-color: rgba(var(--v-primary-base), 0.08) !important;

            &:hover {
                background-color: rgba(var(--v-primary-base), 0.12) !important;
            }
        }

        &.table-row--selectable {
            cursor: pointer;
        }
    }

    ::v-deep .club-info-column {
        padding: 16px !important;
    }

    ::v-deep th {
        font-weight: 600 !important;
        color: rgba(0, 0, 0, 0.87) !important;
        background-color: #fafafa !important;
    }
}

.club-row-content {
    padding: 8px 0;
    border-radius: 4px;
    transition: all 0.2s ease-in-out;

    &--selected {
        background-color: rgba(var(--v-primary-base), 0.08);
    }

    .club-name {
        font-weight: 500;
        font-size: 14px;
        line-height: 1.4;
        color: rgba(0, 0, 0, 0.87);
    }

    .club-id {
        font-size: 12px;
        line-height: 1.3;
        color: rgba(0, 0, 0, 0.6);
        margin-top: 2px;
    }
}

// Club-specific info column styling
.enhanced-data-table {
    ::v-deep .club-info-column {
        padding: 16px !important;
    }
}

// Make rows clickable when in bulk message mode
.enhanced-data-table ::v-deep tbody tr {
    &.table-row--selectable {
        cursor: pointer;

        &:hover .club-row-content {
            background-color: rgba(var(--v-primary-base), 0.04);
        }
    }
}
</style>
